/**
 * ============================================================================
 * sessionService.ts - Enterprise Game Session Service
 * ============================================================================
 */

import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  onSnapshot,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { API_BASE } from '../utils/apiBase';

const SESSIONS_COLLECTION = 'gameSessions';

// ============================================================================
// TYPES
// ============================================================================

export type SessionTransactionType = 
  | 'buy_in'
  | 'cash_out'
  | 'adjustment'
  | 'snapshot';

export type SessionTransactionReason =
  | 'initial'
  | 'rebuy'
  | 'leave'
  | 'table_crash'
  | 'table_end'
  | 'session_end'
  | 'manual'
  | 'correction';

export interface SessionTransaction {
  id: string;
  odD: string;
  displayName: string;
  type: SessionTransactionType;
  amount: number;
  tableId: string;
  tableName?: string;
  timestamp: Date;
  reason: SessionTransactionReason;
  note?: string;
  adjustedBy?: string;
  adjustedByName?: string;
  handNumber?: number;
}

export interface SessionPlayer {
  odD: string;
  displayName: string;
  totalBuyIn: number;
  totalCashOut: number;
  lastKnownStack: number;
  netResult: number;
  isActive: boolean;
  currentTableId?: string;
  joinedAt: Date;
  leftAt?: Date;
  transactions: SessionTransaction[];
  tablesPlayed: string[];
}

export type SessionStatus = 'active' | 'ended';
export type SessionCurrency = 'playcoins' | 'pkcash';

export interface GameSession {
  id: string;
  code: string;
  name: string;
  hostId: string;
  hostName: string;
  currency: SessionCurrency;
  status: SessionStatus;
  createdAt: Date;
  updatedAt: Date;
  endedAt?: Date;
  expiresAt?: Date;
  tableIds: string[];
  currentTableId?: string;
  players: { [odD: string]: SessionPlayer };
  totalHands: number;
  peakPlayerCount: number;
  totalChipsIn: number;
  totalChipsOut: number;
}

export interface SessionSettlement {
  from: string;
  fromName: string;
  to: string;
  toName: string;
  amount: number;
}

export interface SessionExportResult {
  player: string;
  odD: string;
  buyIn: number;
  cashOut: number;
  net: number;
}

export interface SessionExport {
  exportedAt: string;
  version: string;
  session: {
    id: string;
    code: string;
    name: string;
    host: string;
    hostId: string;
    currency: SessionCurrency;
    startedAt: string;
    endedAt?: string;
    duration?: string;
    status: SessionStatus;
  };
  results: SessionExportResult[];
  settlements: SessionSettlement[];
  validation: {
    totalIn: number;
    totalOut: number;
    balanced: boolean;
    difference: number;
  };
  tables: any[];
  transactions: any[];
  handHistory?: any[];
}

export interface TableSessionLink {
  sessionId: string;
  sessionCode: string;
  sessionName: string;
  linkedAt: Date;
}

// ============================================================================
// API UTILITIES
// ============================================================================

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function apiCall<T>(endpoint: string, options: {
  method?: 'GET' | 'POST';
  body?: object;
}): Promise<T> {
  const token = await getAuthToken();
  
  const response = await fetch(API_BASE + endpoint, {
    method: options.method || 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
    },
    body: options.body ? JSON.stringify(options.body) : undefined
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'API call failed: ' + response.status);
  }
  
  return data;
}

// ============================================================================
// SESSION SERVICE CLASS
// ============================================================================

class SessionService {
  
  // ==========================================================================
  // SESSION CREATION
  // ==========================================================================

  async createSession(
    name: string,
    tableId: string,
    currency: 'playcoins' | 'pkcash',
    tableName?: string
  ): Promise<{ success: boolean; sessionId?: string; code?: string; error?: string }> {
    try {
      const result = await apiCall<{ success: boolean; sessionId: string; code: string }>('/session/create', {
        method: 'POST',
        body: { name, tableId, currency }
      });

      console.log('[SESSION] Created session ' + result.sessionId + ' (' + result.code + ')');
      return result;
    } catch (error: any) {
      console.error('[SESSION] Failed to create session:', error);
      return { success: false, error: error.message };
    }
  }

  async addTableToSession(sessionId: string, tableId: string): Promise<boolean> {
    try {
      await apiCall('/session/addTable', {
        method: 'POST',
        body: { sessionId, tableId }
      });

      console.log('[SESSION] Added table ' + tableId + ' to session ' + sessionId);
      return true;
    } catch (error) {
      console.error('[SESSION] Failed to add table to session:', error);
      return false;
    }
  }

  // ==========================================================================
  // SESSION RETRIEVAL
  // ==========================================================================

  async getSession(sessionId: string): Promise<GameSession | null> {
    try {
      const sessionDoc = await getDoc(doc(db, SESSIONS_COLLECTION, sessionId));
      if (!sessionDoc.exists()) return null;
      return this.parseSessionFromFirestore(sessionDoc.data());
    } catch (error) {
      console.error('[SESSION] Failed to get session:', error);
      return null;
    }
  }

  async getSessionByCode(code: string): Promise<GameSession | null> {
    try {
      const q = query(
        collection(db, SESSIONS_COLLECTION),
        where('code', '==', code.toUpperCase()),
        where('status', '==', 'active'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      return this.parseSessionFromFirestore(snapshot.docs[0].data());
    } catch (error) {
      console.error('[SESSION] Failed to get session by code:', error);
      return null;
    }
  }

  async getActiveSessionsForHost(): Promise<GameSession[]> {
    try {
      const user = auth.currentUser;
      if (!user) return [];

      const q = query(
        collection(db, SESSIONS_COLLECTION),
        where('hostId', '==', user.uid),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.parseSessionFromFirestore(doc.data()));
    } catch (error) {
      console.error('[SESSION] Failed to get active sessions:', error);
      return [];
    }
  }

  async getRecentSessions(): Promise<GameSession[]> {
    try {
      const user = auth.currentUser;
      if (!user) return [];

      const twelveHoursAgo = new Date();
      twelveHoursAgo.setHours(twelveHoursAgo.getHours() - 12);

      const q = query(
        collection(db, SESSIONS_COLLECTION),
        where('hostId', '==', user.uid),
        where('status', '==', 'active'),
        where('createdAt', '>', Timestamp.fromDate(twelveHoursAgo)),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.parseSessionFromFirestore(doc.data()));
    } catch (error) {
      console.error('[SESSION] Failed to get recent sessions:', error);
      return [];
    }
  }

  async getSessionForTable(tableId: string): Promise<GameSession | null> {
    try {
      const tableDoc = await getDoc(doc(db, 'tables', tableId));
      if (!tableDoc.exists()) return null;
      
      const tableData = tableDoc.data();
      const sessionLink = tableData?.sessionLink as TableSessionLink | undefined;
      if (!sessionLink?.sessionId) return null;
      
      return this.getSession(sessionLink.sessionId);
    } catch (error) {
      console.error('[SESSION] Failed to get session for table:', error);
      return null;
    }
  }

  // ==========================================================================
  // REAL-TIME SUBSCRIPTION
  // ==========================================================================

  subscribeToSession(
    sessionId: string, 
    callback: (session: GameSession | null) => void
  ): () => void {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    
    return onSnapshot(sessionRef, (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      callback(this.parseSessionFromFirestore(snapshot.data()));
    }, (error) => {
      console.error('[SESSION] Subscription error:', error);
      callback(null);
    });
  }

  // ==========================================================================
  // PLAYER TRACKING
  // ==========================================================================

  async recordBuyIn(
    sessionId: string,
    tableId: string,
    odD: string,
    displayName: string,
    amount: number,
    reason: 'initial' | 'rebuy' = 'initial'
  ): Promise<boolean> {
    try {
      await apiCall('/session/recordBuyIn', {
        method: 'POST',
        body: { sessionId, tableId, odD, displayName, amount, reason }
      });
      console.log('[SESSION] Recorded buy-in: ' + displayName + ' +' + amount + ' (' + reason + ')');
      return true;
    } catch (error) {
      console.error('[SESSION] Failed to record buy-in:', error);
      return false;
    }
  }

  async recordCashOut(
    sessionId: string,
    tableId: string,
    odD: string,
    displayName: string,
    amount: number,
    reason: 'leave' | 'table_crash' | 'table_end' | 'session_end' = 'leave'
  ): Promise<boolean> {
    try {
      await apiCall('/session/recordCashOut', {
        method: 'POST',
        body: { sessionId, tableId, odD, displayName, amount, reason }
      });
      console.log('[SESSION] Recorded cash-out: ' + displayName + ' ' + amount + ' (' + reason + ')');
      return true;
    } catch (error) {
      console.error('[SESSION] Failed to record cash-out:', error);
      return false;
    }
  }

  async updatePlayerStack(sessionId: string, odD: string, currentStack: number): Promise<boolean> {
    try {
      await apiCall('/session/updateStack', {
        method: 'POST',
        body: { sessionId, odD, currentStack }
      });
      return true;
    } catch (error) {
      console.error('[SESSION] Failed to update player stack:', error);
      return false;
    }
  }

  async syncAllStacks(sessionId: string, tableId: string): Promise<boolean> {
    try {
      await apiCall('/session/syncAllStacks', {
        method: 'POST',
        body: { sessionId, tableId }
      });
      console.log('[SESSION] Synced all stacks for session ' + sessionId);
      return true;
    } catch (error) {
      console.error('[SESSION] Failed to sync stacks:', error);
      return false;
    }
  }

  async snapshotAllStacks(
    sessionId: string,
    tableId: string,
    playerStacks: { [odD: string]: { displayName: string; chips: number } },
    reason: 'table_crash' | 'table_end' = 'table_crash'
  ): Promise<boolean> {
    try {
      await apiCall('/session/snapshotStacks', {
        method: 'POST',
        body: { sessionId, tableId, playerStacks, reason }
      });
      console.log('[SESSION] Snapshot saved for ' + Object.keys(playerStacks).length + ' players (' + reason + ')');
      return true;
    } catch (error) {
      console.error('[SESSION] Failed to snapshot stacks:', error);
      return false;
    }
  }

  async manualAdjustment(
    sessionId: string,
    targetUserId: string,
    adjustmentType: 'buy_in' | 'cash_out',
    amount: number,
    note: string
  ): Promise<boolean> {
    try {
      await apiCall('/session/manualAdjustment', {
        method: 'POST',
        body: { sessionId, targetUserId, adjustmentType, amount, note }
      });
      console.log('[SESSION] Manual adjustment: ' + adjustmentType + ' ' + amount);
      return true;
    } catch (error) {
      console.error('[SESSION] Failed to make manual adjustment:', error);
      return false;
    }
  }

  // ==========================================================================
  // SESSION END & EXPORT
  // ==========================================================================

  async endSession(sessionId: string): Promise<SessionExport | null> {
    try {
      const result = await apiCall<{ success: boolean; export: SessionExport }>('/session/end', {
        method: 'POST',
        body: { sessionId }
      });
      console.log('[SESSION] Ended session ' + sessionId);
      return result.export;
    } catch (error) {
      console.error('[SESSION] Failed to end session:', error);
      return null;
    }
  }

  async exportSession(sessionId: string, includeHandHistory: boolean = false): Promise<SessionExport | null> {
    try {
      const result = await apiCall<{ success: boolean; export: SessionExport }>('/session/export', {
        method: 'POST',
        body: { sessionId }
      });
      return result.export;
    } catch (error) {
      console.error('[SESSION] Failed to export session:', error);
      return null;
    }
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  private parseSessionFromFirestore(data: any): GameSession {
    return {
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
      endedAt: data.endedAt?.toDate?.() || (data.endedAt ? new Date(data.endedAt) : undefined),
      expiresAt: data.expiresAt?.toDate?.() || (data.expiresAt ? new Date(data.expiresAt) : undefined),
      players: this.parsePlayersFromFirestore(data.players || {})
    };
  }

  private parsePlayersFromFirestore(players: any): { [odD: string]: SessionPlayer } {
    const parsed: { [odD: string]: SessionPlayer } = {};
    
    for (const [odD, player] of Object.entries(players as Record<string, any>)) {
      parsed[odD] = {
        ...player,
        joinedAt: player.joinedAt?.toDate?.() || new Date(player.joinedAt),
        leftAt: player.leftAt?.toDate?.() || (player.leftAt ? new Date(player.leftAt) : undefined),
        transactions: (player.transactions || []).map((t: any) => ({
          ...t,
          timestamp: t.timestamp?.toDate?.() || new Date(t.timestamp)
        }))
      };
    }
    
    return parsed;
  }

  async incrementHandCount(sessionId: string): Promise<void> {
    try {
      const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (sessionDoc.exists()) {
        const { updateDoc, serverTimestamp } = await import('firebase/firestore');
        const currentCount = sessionDoc.data()?.totalHands || 0;
        await updateDoc(sessionRef, {
          totalHands: currentCount + 1,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('[SESSION] Failed to increment hand count:', error);
    }
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

const sessionService = new SessionService();
export default sessionService;